import type { StreamChatParams } from './streamChat';

/**
 * Mock is OFF by default. Enable it via localStorage:
 *   localStorage.setItem('rai_mock', 'true')   // use mock stream
 *   localStorage.removeItem('rai_mock')         // back to real API
 */
export function isMockEnabled(): boolean {
  return false;
}

// ---------------------------------------------------------------------------
// Response library — pick a scenario based on message keywords
// ---------------------------------------------------------------------------

interface MockScenario {
  brief?: string;
  body: string;
  useTool?: string;
}

function pickScenario(message: string): MockScenario {
  const q = message.toLowerCase();

  if (q.includes('inconsisten') || q.includes('conflict')) {
    return {
      brief: 'Found 2 potential conflicts across linked RSD documents.',
      body: [
        '| DOCUMENT | FIELD | CONFLICT |\n',
        '| --- | --- | --- |\n',
        '| RSD-Auth | Due Date | Mismatches ARM-830 target |\n',
        '| RSD-Payments | Approver | Listed as vacant |\n\n',
        'Review each document to resolve the discrepancy.'
      ].join(''),
      useTool: 'document_search'
    };
  }

  if (q.includes('update') || q.includes('last 7') || q.includes('week')) {
    return {
      brief: 'Here are the updates from the last 7 days.',
      body: [
        '| ISSUE ID | CHANGE | UPDATED BY | DATE |\n',
        '| --- | --- | --- | --- |\n',
        '| ARM-831 | Status → In Review | Sarah K. | Apr 28 |\n',
        '| ARM-835 | Due date moved | David W. | Apr 29 |\n',
        '| ARM-840 | New milestone added | Alex R. | Apr 30 |\n\n',
        'Showing 3 of 7 updates.'
      ].join(''),
      useTool: 'roadmap_query'
    };
  }

  if (
    q.includes('past due') ||
    q.includes('overdue') ||
    q.includes('critical')
  ) {
    return {
      brief: 'Found 3 critical milestones past their approval date.',
      body: [
        '| ISSUE ID | MILESTONE | APPROVER | DAYS OVERDUE |\n',
        '| --- | --- | --- | --- |\n',
        '| ARM-820 | Brand launch | David W. | 5 days |\n',
        '| ARM-822 | GTM handoff | Sarah K. | 3 days |\n',
        '| ARM-828 | Legal sign-off | Chris M. | 3 days |\n\n',
        '**Action recommended:** Escalate ARM-820 immediately.'
      ].join(''),
      useTool: 'roadmap_query'
    };
  }

  if (q.includes('pdf') || q.includes('report') || q.includes('download')) {
    return {
      body: [
        "I can't generate a PDF directly, but here's a full milestone status summary you can copy:\n\n",
        '| ISSUE ID | MILESTONE | STATUS | APPROVER |\n',
        '| --- | --- | --- | --- |\n',
        '| ARM-830 | GTMS | Pending | David W. |\n',
        '| ARM-831 | BRND | In Review | Sarah K. |\n',
        '| ARM-832 | INTL | Approved | Alex R. |\n\n',
        'Use **Download CSV** to export this table.'
      ].join('')
    };
  }

  // Default — milestone approval query
  return {
    brief: 'Here are the milestones out for approval by David.',
    body: [
      '| ISSUE ID | MILESTONE | APPROVER | DATE |\n',
      '| --- | --- | --- | --- |\n',
      '| ARM-830 | GTMS | David Wadhwani | Q4 |\n',
      '| ARM-830 | BRND | David Wadhwani | Apr 12, 2026 |\n\n',
      'Showing 2 of 2 results.\n\n',
      '**Suggested next questions:**\n',
      '- 🚀 What is the current status of ARM-830?\n',
      '- 📅 What are the upcoming release dates for the next quarter?\n',
      '- ⚠️ Which releases are currently at risk?\n',
      '- 💡 What are the most common reasons for past-due releases?'
    ].join(''),
    useTool: 'roadmap_query'
  };
}

// ---------------------------------------------------------------------------

/** Emits the same callbacks as the SSE stream for local UI testing. */
export function deliverMockStreamResponse(params: StreamChatParams) {
  const {
    message,
    conversationId,
    onMessageStart,
    onToken,
    onDone,
    onError,
    signal
  } = params;

  const scenario = pickScenario(message);
  const briefBlock = scenario.brief
    ? `:::brief\n${scenario.brief}\n:::\n\n`
    : '';
  const fullText = briefBlock + scenario.body;

  const sleep = (ms: number) =>
    new Promise<void>((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const id = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) reject(new DOMException('Aborted', 'AbortError'));
        else resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });

  void (async () => {
    try {
      onMessageStart(`mock-${Date.now()}`);

      if (scenario.useTool && params.onToolStart) {
        await sleep(300);
        params.onToolStart(scenario.useTool);
        await sleep(600);
      }

      for (const ch of fullText) {
        await sleep(12);
        onToken(ch);
      }

      await sleep(20);
      onDone(conversationId ?? `mock-conv-${Date.now()}`, 'mock-session');
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      onError(e instanceof Error ? e.message : 'Mock stream failed');
    }
  })();
}
